import { useState, useMemo, useCallback } from "react";
import { useHR } from "../context/HRContext";
import { LEAVE_STATUS, DEFAULT_LEAVE_BALANCE } from "../constants/hrConstants";

export function useLeaves() {
  const { employees, leaves, applyLeave, updateLeaveStatus, deleteLeave } = useHR();

  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterEmp, setFilterEmp] = useState("");
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  const [formData, setFormData] = useState({
    employeeId: "", employeeName: "", leaveType: "casual", durationType: "long_leave", fromDate: "", toDate: "",
    days: 1, reason: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const filtered = useMemo(() => {
    return leaves.filter((l) => {
      const q = filterEmp.toLowerCase();
      const matchEmp = !q || l.employeeName?.toLowerCase().includes(q) || l.employeeId?.toLowerCase().includes(q);
      const matchStatus = !filterStatus || l.status === filterStatus;
      const matchType = !filterType || l.leaveType === filterType;
      return matchEmp && matchStatus && matchType;
    });
  }, [leaves, filterStatus, filterType, filterEmp]);

  const stats = useMemo(() => ({
    total: leaves.length,
    pending: leaves.filter((l) => l.status === LEAVE_STATUS.PENDING).length,
    approved: leaves.filter((l) => l.status === LEAVE_STATUS.APPROVED).length,
    rejected: leaves.filter((l) => l.status === LEAVE_STATUS.REJECTED).length,
  }), [leaves]);

  // Leave balance per employee (mock calculation)
  const getLeaveBalance = useCallback((employeeId) => {
    const taken = {};
    leaves
      .filter((l) => l.employeeId === employeeId && l.status === LEAVE_STATUS.APPROVED)
      .forEach((l) => {
        taken[l.leaveType] = (taken[l.leaveType] || 0) + l.days;
      });
    return Object.entries(DEFAULT_LEAVE_BALANCE).map(([type, total]) => ({
      type,
      total,
      taken: taken[type] || 0,
      balance: total - (taken[type] || 0),
    }));
  }, [leaves]);

  const validate = (data) => {
    const errs = {};
    if (!data.employeeId) errs.employeeId = "Employee is required";
    if (!data.leaveType) errs.leaveType = "Leave type is required";
    if (!data.fromDate) errs.fromDate = "From date is required";
    if ((data.durationType || "long_leave") === "long_leave") {
      if (!data.toDate) errs.toDate = "To date is required";
      if (data.fromDate && data.toDate && data.toDate < data.fromDate)
        errs.toDate = "To date must be after From date";
    }
    if (!data.reason?.trim()) errs.reason = "Reason is required";
    return errs;
  };

  const calcDays = (from, to, durationType = "long_leave") => {
    if (durationType === "half_day") return 0.5;
    if (!from || !to) return 1;
    const diff = (new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24) + 1;
    return Math.max(1, diff);
  };

  const openApply = useCallback(() => {
    setFormData({ employeeId: "", employeeName: "", leaveType: "casual", durationType: "long_leave", fromDate: "", toDate: "", days: 1, reason: "" });
    setFormErrors({});
    setIsApplyOpen(true);
  }, []);

  const closeApply = useCallback(() => {
    setIsApplyOpen(false);
    setFormErrors({});
  }, []);

  const updateField = useCallback((field, value) => {
    setFormData((p) => {
      const next = { ...p, [field]: value };
      
      // If durationType changes to half_day, automatically align toDate to match fromDate and default to today
      if (field === "durationType") {
        if (value === "half_day") {
          const today = (() => {
            const d = new Date();
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          })();
          next.fromDate = today;
          next.toDate = today;
        }
      }
      // If fromDate changes for half_day, also update toDate
      if (field === "fromDate" && next.durationType === "half_day") {
        next.toDate = value;
      }
      
      next.days = calcDays(next.fromDate, next.toDate, next.durationType);
      return next;
    });
    setFormErrors((p) => ({ ...p, [field]: undefined }));
  }, []);

  const save = useCallback(async () => {
    const errs = validate(formData);
    if (Object.keys(errs).length) { setFormErrors(errs); return false; }
    setIsSaving(true);
    try {
      await new Promise((r) => setTimeout(r, 350));
      const emp = employees.find((e) => e.id === formData.employeeId);
      applyLeave({ 
        ...formData, 
        employeeName: emp?.name || "", 
        days: calcDays(formData.fromDate, formData.toDate, formData.durationType) 
      });
      closeApply();
      return true;
    } finally {
      setIsSaving(false);
    }
  }, [formData, applyLeave, employees, closeApply]);

  const approve = useCallback(async (id) => {
    await new Promise((r) => setTimeout(r, 200));
    updateLeaveStatus(id, LEAVE_STATUS.APPROVED);
  }, [updateLeaveStatus]);

  const reject = useCallback(async (id, remarks = "") => {
    await new Promise((r) => setTimeout(r, 200));
    updateLeaveStatus(id, LEAVE_STATUS.REJECTED, remarks);
  }, [updateLeaveStatus]);

  const cancel = useCallback(async (id) => {
    await new Promise((r) => setTimeout(r, 200));
    updateLeaveStatus(id, LEAVE_STATUS.REJECTED);
  }, [updateLeaveStatus]);

  return {
    leaves: filtered, allLeaves: leaves,
    filterStatus, setFilterStatus, filterType, setFilterType,
    filterEmp, setFilterEmp,
    stats, employees, isApplyOpen, isSaving,
    selectedLeave, setSelectedLeave,
    formData, formErrors,
    openApply, closeApply, updateField, save,
    approve, reject, cancel, deleteLeave, getLeaveBalance,
  };
}
